// src/App.js
import { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import Loading from "./components/Loading";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

// Lazy load components that are not immediately visible
const ChatBot = lazy(() => import("./components/ChatBot"));
const DebugInfo = lazy(() => import("./components/DebugInfo"));

// Lazy load pages with prefetch
const Home = lazy(() => import(/* webpackPrefetch: true */ "./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Team = lazy(() => import("./pages/Team"));
const Appointment = lazy(() =>
  import(/* webpackPrefetch: true */ "./pages/Appointment")
);
const Insights = lazy(() => import("./pages/Insights"));
const BlogPostDetail = lazy(() => import("./pages/BlogPostDetail"));
const Training = lazy(() => import("./pages/Training"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));

// Minimal loading component
const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600"></div>
  </div>
);

// Layout components
const PublicLayout = ({ children }) => (
  <div className="App min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-grow">{children}</main>
    <Footer />
    <Suspense fallback={null}>
      <ChatBot />
    </Suspense>
  </div>
);

const AdminLayout = ({ children }) => (
  <div className="admin-app">{children}</div>
);

// Route components
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Loading />;
  return !isAuthenticated ? children : <Navigate to="/admin" replace />;
};

function AppContent() {
  const [appLoading, setAppLoading] = useState(true);
  const [loadingComplete, setLoadingComplete] = useState(false);

  const handleLoadingComplete = () => {
    setLoadingComplete(true);
  };

  useEffect(() => {
    // Only set app loading to false when resources are ready
    if (loadingComplete) {
      setAppLoading(false);
    }
  }, [loadingComplete]);

  if (appLoading) {
    return <Loading onLoadingComplete={handleLoadingComplete} />;
  }

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          {/* Public Routes */}
          <Route
            path="/*"
            element={
              <PublicLayout>
                <Suspense fallback={<PageLoading />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/team" element={<Team />} />
                    <Route path="/appointment" element={<Appointment />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/training" element={<Training />} />
                    <Route path="/services/:id" element={<ServiceDetail />} />
                    <Route
                      path="/insights/:slug"
                      element={<BlogPostDetail />}
                    />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </PublicLayout>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <AdminLayout>
                <Suspense fallback={<PageLoading />}>
                  <Routes>
                    <Route
                      path="/login"
                      element={
                        <PublicRoute>
                          <AdminLogin />
                        </PublicRoute>
                      }
                    />
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </AdminLayout>
            }
          />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={null}>{/* <DebugInfo /> */}</Suspense>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
